import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Copy, Tag, Users, Calendar, 
  Percent, Gift, ArrowLeft, CheckCircle, XCircle,
  Clock, Eye, EyeOff, DollarSign, CreditCard, ClipboardList, AlertTriangle, Flag,
  GraduationCap, TrendingUp, Mail, Send, Newspaper, Edit, Pin, Moon, Smartphone, FileText, Download, Activity, Crown, ListTree
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList,
} from 'recharts';
import { formatLocalDate } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPromoCodesPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [promoCodes, setPromoCodes] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [issueReports, setIssueReports] = useState([]);
  const [teacherFunnel, setTeacherFunnel] = useState(null);
  const [newsPosts, setNewsPosts] = useState([]);
  const [lunaWaitlist, setLunaWaitlist] = useState([]);
  const [lunaSearch, setLunaSearch] = useState('');

  // Apple IAP subscriptions tab state
  const [iapData, setIapData] = useState(null);
  const [iapLoading, setIapLoading] = useState(false);
  const [iapSearch, setIapSearch] = useState('');
  const [iapStatusFilter, setIapStatusFilter] = useState('all');
  const [iapDateFrom, setIapDateFrom] = useState('');
  const [iapDateTo, setIapDateTo] = useState('');
  const [iapDetailUser, setIapDetailUser] = useState(null);
  const [iapDetailHistory, setIapDetailHistory] = useState(null);
  const [iapRefreshing, setIapRefreshing] = useState(false);
  // Legal Audit tab state
  const [legalSummary, setLegalSummary] = useState(null);
  const [legalLoading, setLegalLoading] = useState(false);
  const [legalExporting, setLegalExporting] = useState(false);
  const [legalStartDate, setLegalStartDate] = useState('');
  const [legalEndDate, setLegalEndDate] = useState('');
  const [legalEmailFilter, setLegalEmailFilter] = useState('');
  // Activation Funnel tab state
  const [funnelData, setFunnelData] = useState(null);
  const [funnelDays, setFunnelDays] = useState(30);
  const [funnelLoading, setFunnelLoading] = useState(false);
  // Subscriptions tab state
  const [subsData, setSubsData] = useState(null);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsStatus, setSubsStatus] = useState('active');
  const [subsSource, setSubsSource] = useState('all');
  const [subsSearch, setSubsSearch] = useState('');
  // All Transactions tab state
  const [allTxData, setAllTxData] = useState(null);
  const [allTxLoading, setAllTxLoading] = useState(false);
  const [allTxSource, setAllTxSource] = useState('all');
  const [allTxTier, setAllTxTier] = useState('all');
  const [allTxSearch, setAllTxSearch] = useState('');
  const [allTxStart, setAllTxStart] = useState('');
  const [allTxEnd, setAllTxEnd] = useState('');
  const [lunaNotifyOpen, setLunaNotifyOpen] = useState(false);
  const [lunaEmailSubject, setLunaEmailSubject] = useState('');
  const [lunaEmailHtml, setLunaEmailHtml] = useState('');
  const [lunaOnlyPending, setLunaOnlyPending] = useState(true);
  const [lunaSending, setLunaSending] = useState(false);
  const [lunaSendResult, setLunaSendResult] = useState(null);
  const [editingPost, setEditingPost] = useState(null); // null = none, 'new' = create, or post object
  const [postForm, setPostForm] = useState({ title: '', body: '', category: 'new_feature', is_published: true, is_pinned: false });
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [activeTab, setActiveTab] = useState('purchases');
  
  const [newCode, setNewCode] = useState({
    code: '',
    discount_percent: 100,
    tier_granted: 'tier2',
    duration_days: 365,
    max_uses: 1,
    expires_at: '',
    description: ''
  });

  useEffect(() => {
    fetchPromoCodes();
    fetchRedemptions();
    fetchPurchases();
    fetchWaitlist();
    fetchIssueReports();
    fetchTeacherFunnel();
    fetchNewsPosts();
    fetchLunaWaitlist();
    fetchIapSubscriptions();
  }, []);

  // Refetch IAP when filters change
  useEffect(() => {
    if (activeTab === 'apple-iap') {
      fetchIapSubscriptions();
    }
  }, [iapStatusFilter, iapDateFrom, iapDateTo]); // eslint-disable-line

  // Load Legal Audit summary when its tab is opened
  useEffect(() => {
    if (activeTab === 'legal-audit' && legalSummary === null && !legalLoading) {
      fetchLegalSummary();
    }
  }, [activeTab]); // eslint-disable-line

  // Load Activation Funnel data when its tab is opened or the window changes
  useEffect(() => {
    if (activeTab === 'activation-funnel') {
      fetchActivationFunnel();
    }
  }, [activeTab, funnelDays]); // eslint-disable-line

  // Load Subscriptions list whenever filters change
  useEffect(() => {
    if (activeTab === 'subscriptions') {
      fetchSubscriptions();
    }
  }, [activeTab, subsStatus, subsSource, subsSearch]); // eslint-disable-line

  // Load All Transactions whenever filters change
  useEffect(() => {
    if (activeTab === 'all-transactions') {
      fetchAllTransactions();
    }
  }, [activeTab, allTxSource, allTxTier, allTxSearch, allTxStart, allTxEnd]); // eslint-disable-line

  const fetchAllTransactions = async () => {
    setAllTxLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('source', allTxSource);
      params.set('tier', allTxTier);
      if (allTxSearch.trim()) params.set('search', allTxSearch.trim());
      if (allTxStart) params.set('start_date', allTxStart);
      if (allTxEnd) params.set('end_date', allTxEnd);
      const res = await fetch(`${API_URL}/api/subscriptions/admin/transactions/all?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setAllTxData(await res.json());
      } else {
        toast.error('Failed to load transactions');
      }
    } catch (err) {
      toast.error('Network error loading transactions');
    } finally {
      setAllTxLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    setSubsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('status', subsStatus);
      params.set('source', subsSource);
      if (subsSearch.trim()) params.set('search', subsSearch.trim());
      const res = await fetch(`${API_URL}/api/subscriptions/admin/subscriptions?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setSubsData(await res.json());
      } else {
        toast.error('Failed to load subscriptions');
      }
    } catch (err) {
      toast.error('Network error loading subscriptions');
    } finally {
      setSubsLoading(false);
    }
  };

  const fetchActivationFunnel = async () => {
    setFunnelLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/activation-funnel?days=${funnelDays}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) {
        setFunnelData(await res.json());
      } else {
        toast.error('Failed to load activation funnel');
      }
    } catch (err) {
      toast.error('Network error loading activation funnel');
    } finally {
      setFunnelLoading(false);
    }
  };

  const fetchLegalSummary = async () => {
    setLegalLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/legal-disclaimers/admin/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setLegalSummary(await res.json());
      } else {
        toast.error('Failed to load legal audit summary');
      }
    } catch (err) {
      toast.error('Network error loading legal audit summary');
    } finally {
      setLegalLoading(false);
    }
  };

  const downloadLegalAuditCsv = async () => {
    setLegalExporting(true);
    try {
      const params = new URLSearchParams();
      if (legalStartDate) params.set('start_date', legalStartDate);
      if (legalEndDate) params.set('end_date', legalEndDate);
      if (legalEmailFilter.trim()) params.set('email', legalEmailFilter.trim());
      const url = `${API_URL}/api/legal-disclaimers/admin/export${
        params.toString() ? `?${params.toString()}` : ''
      }`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error('Export failed');
        return;
      }
      const rowCount = res.headers.get('X-Audit-Row-Count');
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      a.download = `legal_audit_export_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(rowCount ? `Exported ${rowCount} row(s)` : 'Export complete');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setLegalExporting(false);
    }
  };

  const fetchLunaWaitlist = async () => {
    try {
      const res = await fetch(`${API_URL}/api/luna-waitlist/admin/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLunaWaitlist(data.entries || []);
      }
    } catch (err) { /* silent */ }
  };

  const fetchIapSubscriptions = async () => {
    setIapLoading(true);
    try {
      const params = new URLSearchParams();
      if (iapStatusFilter && iapStatusFilter !== 'all') params.set('status', iapStatusFilter);
      if (iapSearch.trim()) params.set('search', iapSearch.trim());
      if (iapDateFrom) params.set('date_from', iapDateFrom);
      if (iapDateTo) params.set('date_to', iapDateTo);
      params.set('page_size', '200');
      const res = await fetch(`${API_URL}/api/apple-iap/admin/subscriptions?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setIapData(await res.json());
      } else {
        toast.error('Failed to load Apple subscriptions');
      }
    } catch (err) {
      toast.error('Network error loading Apple subscriptions');
    } finally {
      setIapLoading(false);
    }
  };

  const openIapDetail = async (userId, userEmail) => {
    setIapDetailUser({ id: userId, email: userEmail });
    setIapDetailHistory(null);
    try {
      const res = await fetch(`${API_URL}/api/apple-iap/admin/subscriptions/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setIapDetailHistory(await res.json());
      }
    } catch (err) { /* silent */ }
  };

  const refreshIapFromApple = async () => {
    if (!iapDetailUser) return;
    setIapRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/apple-iap/admin/subscriptions/${iapDetailUser.id}/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Refreshed from Apple');
        setIapDetailHistory(data);
        // Also refresh the main list so any status changes are reflected
        fetchIapSubscriptions();
      } else {
        toast.error(data.detail || 'Refresh failed');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setIapRefreshing(false);
    }
  };

  const exportLunaCsv = () => {
    if (lunaWaitlist.length === 0) {
      toast.error('No entries to export');
      return;
    }
    const rows = [
      ['email', 'name', 'source', 'created_at', 'notified'],
      ...lunaWaitlist.map(e => [
        e.email,
        e.name || '',
        e.source || '',
        e.created_at || '',
        e.notified ? 'true' : 'false',
      ]),
    ];
    const csv = rows.map(r =>
      r.map(cell => {
        const s = String(cell ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `luna-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  const copyAllLunaEmails = async () => {
    if (lunaWaitlist.length === 0) {
      toast.error('No entries to copy');
      return;
    }
    const emails = lunaWaitlist.map(e => e.email).join(', ');
    try {
      await navigator.clipboard.writeText(emails);
      toast.success(`Copied ${lunaWaitlist.length} email${lunaWaitlist.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const openNotifyDialog = async () => {
    if (lunaWaitlist.length === 0) {
      toast.error('No one to notify yet');
      return;
    }
    setLunaSendResult(null);
    setLunaNotifyOpen(true);
    // Load default email template
    try {
      const res = await fetch(`${API_URL}/api/luna-waitlist/admin/launch-email-preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (!lunaEmailSubject) setLunaEmailSubject(data.subject || '');
        if (!lunaEmailHtml) setLunaEmailHtml(data.html_body || '');
      }
    } catch (err) { /* silent */ }
  };

  const sendLunaNotification = async (dryRun) => {
    if (!lunaEmailSubject.trim() || !lunaEmailHtml.trim()) {
      toast.error('Subject and email body are required');
      return;
    }
    if (!dryRun) {
      const pendingCount = lunaOnlyPending
        ? lunaWaitlist.filter(e => !e.notified).length
        : lunaWaitlist.length;
      const ok = window.confirm(
        `Send the launch email to ${pendingCount} recipient${pendingCount === 1 ? '' : 's'}?\n\nThis cannot be undone.`
      );
      if (!ok) return;
    }
    setLunaSending(true);
    setLunaSendResult(null);
    try {
      const res = await fetch(`${API_URL}/api/luna-waitlist/admin/notify-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: lunaEmailSubject,
          html_body: lunaEmailHtml,
          only_pending: lunaOnlyPending,
          dry_run: !!dryRun,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to send');
      } else {
        setLunaSendResult(data);
        if (data.dry_run) {
          toast.success(`Dry run complete — ${data.total} recipient(s) ready`);
        } else {
          toast.success(`Sent ${data.sent} email(s)${data.failed ? `, ${data.failed} failed` : ''}`);
          // Refresh list to show updated "notified" status
          fetchLunaWaitlist();
        }
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setLunaSending(false);
    }
  };

  const fetchNewsPosts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/news/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNewsPosts(data.posts || []);
      }
    } catch (err) { /* silent */ }
  };

  const startNewPost = () => {
    setPostForm({ title: '', body: '', category: 'new_feature', is_published: true, is_pinned: false });
    setEditingPost('new');
  };

  const startEditPost = (post) => {
    setPostForm({
      title: post.title,
      body: post.body,
      category: post.category,
      is_published: post.is_published,
      is_pinned: post.is_pinned,
    });
    setEditingPost(post);
  };

  const savePost = async () => {
    if (!postForm.title.trim() || !postForm.body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    try {
      if (editingPost === 'new') {
        await fetch(`${API_URL}/api/news`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(postForm),
        }).then(r => { if (!r.ok) throw new Error(); });
        toast.success('Post created');
      } else {
        await fetch(`${API_URL}/api/news/${editingPost.id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(postForm),
        }).then(r => { if (!r.ok) throw new Error(); });
        toast.success('Post updated');
      }
      setEditingPost(null);
      fetchNewsPosts();
    } catch (e) {
      toast.error('Failed to save post');
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this post permanently?')) return;
    try {
      await fetch(`${API_URL}/api/news/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      toast.success('Post deleted');
      fetchNewsPosts();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const fetchTeacherFunnel = async () => {
    try {
      const res = await fetch(`${API_URL}/api/teacher/admin/activation-funnel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTeacherFunnel(data);
      }
    } catch (err) {
      console.error('Failed to load teacher funnel');
    }
  };

  const fetchIssueReports = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reports/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIssueReports(data.reports || []);
      }
    } catch (err) {
      console.error('Failed to load issue reports');
    }
  };

  const handleUpdateReportStatus = async (reportId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/reports/admin/${reportId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        fetchIssueReports();
        toast.success('Report updated');
      }
    } catch (err) {
      toast.error('Failed to update report');
    }
  };

  const fetchWaitlist = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/tier3-waitlist/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWaitlist(data.entries || []);
      }
    } catch (err) {
      console.error('Failed to load waitlist');
    }
  };

  const fetchPurchases = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/purchases`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPurchases(data.purchases || []);
      }
    } catch (err) {
      console.error('Failed to load purchases');
    }
  };

  const fetchPromoCodes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/promo-codes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPromoCodes(data.promo_codes || []);
      } else if (res.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const fetchRedemptions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/promo-codes/redemptions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRedemptions(data.redemptions || []);
      }
    } catch (err) {
      console.error('Failed to load redemptions');
    }
  };

  const handleCreateCode = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...newCode,
        expires_at: newCode.expires_at ? new Date(newCode.expires_at).toISOString() : null
      };
      
      const res = await fetch(`${API_URL}/api/subscriptions/promo-codes/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(`Promo code "${newCode.code.toUpperCase()}" created!`);
        setShowCreateForm(false);
        setNewCode({
          code: '',
          discount_percent: 100,
          tier_granted: 'tier2',
          duration_days: 365,
          max_uses: 1,
          expires_at: '',
          description: ''
        });
        fetchPromoCodes();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to create code');
      }
    } catch (err) {
      toast.error('Failed to create promo code');
    }
  };

  const handleDeleteCode = async (code) => {
    if (!confirm(`Deactivate promo code "${code}"?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/promo-codes/${code}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success(`Promo code "${code}" deactivated`);
        fetchPromoCodes();
      } else {
        toast.error('Failed to deactivate code');
      }
    } catch (err) {
      toast.error('Failed to deactivate code');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard!');
  };

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewCode({ ...newCode, code });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-promo-codes">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-[#57534E]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-nunito font-bold text-[#2D2D2D]">Promo Code Management</h1>
            <p className="text-sm text-[#57534E]">Create and manage discount codes</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate('/admin/stripe-price-check')}
            variant="outline"
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid="open-stripe-check-btn"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Stripe Price Check
          </Button>
          <Button
            onClick={() => setShowCreateForm(true)}
            className="bg-primary text-white"
            data-testid="create-promo-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Code
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-stone-200 overflow-x-auto">
        <button
          onClick={() => setActiveTab('purchases')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'purchases'
              ? 'border-primary text-primary'
              : 'border-transparent text-[#57534E] hover:text-[#2D2D2D]'
          }`}
        >
          <DollarSign className="w-4 h-4 inline mr-2" />
          Purchases ({purchases.length})
        </button>
        <button
          onClick={() => setActiveTab('subscriptions')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'subscriptions'
              ? 'border-primary text-primary'
              : 'border-transparent text-[#57534E] hover:text-[#2D2D2D]'
          }`}
          data-testid="admin-tab-subscriptions"
        >
          <Crown className="w-4 h-4 inline mr-2" />
          Subscriptions{subsData ? ` (${subsData.totals.active})` : ''}
        </button>
        <button
          onClick={() => setActiveTab('all-transactions')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'all-transactions'
              ? 'border-primary text-primary'
              : 'border-transparent text-[#57534E] hover:text-[#2D2D2D]'
          }`}
          data-testid="admin-tab-all-transactions"
        >
          <ListTree className="w-4 h-4 inline mr-2" />
          All Transactions{allTxData ? ` (${allTxData.totals.total})` : ''}
        </button>
        <button
          onClick={() => setActiveTab('reports')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'reports'
              ? 'border-primary text-primary'
              : 'border-transparent text-[#57534E] hover:text-[#2D2D2D]'
          }`}
        >
          <AlertTriangle className="w-4 h-4 inline mr-2" />
          Reports ({issueReports.filter(r => r.status === 'open').length})
        </button>
        <button
          onClick={() => setActiveTab('waitlist')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'waitlist'
              ? 'border-primary text-primary'
              : 'border-transparent text-[#57534E] hover:text-[#2D2D2D]'
          }`}
        >
          <ClipboardList className="w-4 h-4 inline mr-2" />
          Pro Waitlist ({waitlist.length})
        </button>
        <button
          onClick={() => setActiveTab('codes')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'codes'
              ? 'border-primary text-primary'
              : 'border-transparent text-[#57534E] hover:text-[#2D2D2D]'
          }`}
        >
          <Tag className="w-4 h-4 inline mr-2" />
          Promo Codes ({promoCodes.length})
        </button>
        <button
          onClick={() => setActiveTab('redemptions')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'redemptions'
              ? 'border-primary text-primary'
              : 'border-transparent text-[#57534E] hover:text-[#2D2D2D]'
          }`}
        >
          <Gift className="w-4 h-4 inline mr-2" />
          Redemptions ({redemptions.length})
        </button>
        <button
          onClick={() => setActiveTab('teacher-funnel')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'teacher-funnel'
              ? 'border-primary text-primary'
              : 'border-transparent text-[#57534E] hover:text-[#2D2D2D]'
          }`}
          data-testid="admin-tab-teacher-funnel"
        >
          <GraduationCap className="w-4 h-4 inline mr-2" />
          Teacher Funnel{teacherFunnel ? ` (${teacherFunnel.summary.total_invites})` : ''}
        </button>
        <button
          onClick={() => setActiveTab('news')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'news'
              ? 'border-primary text-primary'
              : 'border-transparent text-[#57534E] hover:text-[#2D2D2D]'
          }`}
          data-testid="admin-tab-news"
        >
          <Newspaper className="w-4 h-4 inline mr-2" />
          News Posts ({newsPosts.length})
        </button>
        <button
          onClick={() => setActiveTab('luna-waitlist')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'luna-waitlist'
              ? 'border-primary text-primary'
              : 'border-transparent text-[#57534E] hover:text-[#2D2D2D]'
          }`}
          data-testid="admin-tab-luna-waitlist"
        >
          <Moon className="w-4 h-4 inline mr-2" />
          Luna Waitlist ({lunaWaitlist.length})
        </button>
        <button
          onClick={() => setActiveTab('apple-iap')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'apple-iap'
              ? 'border-primary text-primary'
              : 'border-transparent text-[#57534E] hover:text-[#2D2D2D]'
          }`}
          data-testid="admin-tab-apple-iap"
        >
          <Smartphone className="w-4 h-4 inline mr-2" />
          Apple Subscriptions{iapData ? ` (${iapData.total})` : ''}
        </button>
        <button
          onClick={() => setActiveTab('legal-audit')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'legal-audit'
              ? 'border-primary text-primary'
              : 'border-transparent text-[#57534E] hover:text-[#2D2D2D]'
          }`}
          data-testid="admin-tab-legal-audit"
        >
          <FileText className="w-4 h-4 inline mr-2" />
          Legal Audit{legalSummary ? ` (${legalSummary.total_acceptances})` : ''}
        </button>
        <button
          onClick={() => setActiveTab('activation-funnel')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'activation-funnel'
              ? 'border-primary text-primary'
              : 'border-transparent text-[#57534E] hover:text-[#2D2D2D]'
          }`}
          data-testid="admin-tab-activation-funnel"
        >
          <Activity className="w-4 h-4 inline mr-2" />
          Activation Funnel{funnelData ? ` (${funnelData.totals.signed_up})` : ''}
        </button>
      </div>

      {/* Recent Purchases List */}
      {activeTab === 'purchases' && (
        <div className="grid gap-4">
          {purchases.length === 0 ? (
            <Card className="p-8 text-center">
              <CreditCard className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-[#57534E]">No purchases yet. When someone subscribes, they'll appear here!</p>
            </Card>
          ) : (
            purchases.map((purchase, i) => (
              <Card key={i} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#2D2D2D]">{purchase.user_email}</p>
                      <p className="text-sm text-[#57534E]">
                        {purchase.tier === 'tier1' ? 'Essential' : 'Premium'} - {purchase.interval}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">${purchase.amount}</p>
                    <p className="text-xs text-[#57534E]">
                      {formatLocalDate(purchase.completed_at || purchase.created_at)}
                    </p>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Issue Reports */}
      {activeTab === 'reports' && (
        <div className="grid gap-4">
          {issueReports.length === 0 ? (
            <Card className="p-8 text-center">
              <Flag className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-[#57534E]">No issue reports yet.</p>
            </Card>
          ) : (
            issueReports.map((report, i) => (
              <Card key={i} className={`p-4 ${report.status === 'open' ? 'border-l-4 border-l-red-500' : report.status === 'in_progress' ? 'border-l-4 border-l-yellow-500' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                        report.report_type === 'bug' ? 'bg-red-100 text-red-700' :
                        report.report_type === 'feature_request' ? 'bg-blue-100 text-blue-700' :
                        report.report_type === 'content_issue' ? 'bg-orange-100 text-orange-700' :
                        'bg-stone-100 text-stone-700'
                      }`}>
                        {report.report_type?.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                        report.status === 'open' ? 'bg-red-100 text-red-700' :
                        report.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                        report.status === 'resolved' ? 'bg-green-100 text-green-700' :
                        'bg-stone-100 text-stone-700'
                      }`}>
                        {report.status?.toUpperCase()}
                      </span>
                    </div>
                    <h3 className="font-semibold text-[#2D2D2D]">{report.title}</h3>
                    <p className="text-sm text-[#57534E] mt-1">{report.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-[#78716C]">
                      <span>From: {report.user_email}</span>
                      <span>{formatLocalDate(report.created_at)}</span>
                      {report.page_url && <span>Page: {report.page_url}</span>}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <select
                      value={report.status}
                      onChange={(e) => handleUpdateReportStatus(report.id, e.target.value)}
                      className="text-xs border rounded px-2 py-1"
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Pro Waitlist */}
      {activeTab === 'waitlist' && (
        <div className="grid gap-4">
          {waitlist.length === 0 ? (
            <Card className="p-8 text-center">
              <ClipboardList className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-[#57534E]">No one on the Pro waitlist yet.</p>
            </Card>
          ) : (
            waitlist.map((entry, i) => (
              <Card key={i} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-purple-600 font-bold">#{entry.position}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-[#2D2D2D]">{entry.name || 'No name'}</p>
                      <p className="text-sm text-[#57534E]">{entry.email}</p>
                      {entry.interest_notes && (
                        <p className="text-xs text-[#78716C] mt-1 italic">"{entry.interest_notes}"</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#57534E]">
                      {formatLocalDate(entry.created_at)}
                    </p>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Promo Codes List */}
      {activeTab === 'codes' && (
        <div className="grid gap-4">
          {promoCodes.length === 0 ? (
            <Card className="p-8 text-center">
              <Tag className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-[#57534E]">No promo codes yet. Create your first one!</p>
            </Card>
          ) : (
            promoCodes.map((promo) => (
              <Card key={promo.code} className={`p-4 ${!promo.is_active ? 'opacity-50' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-lg font-bold text-primary bg-primary/10 px-3 py-1 rounded">
                        {promo.code}
                      </span>
                      <button
                        onClick={() => copyCode(promo.code)}
                        className="text-stone-400 hover:text-primary"
                        title="Copy code"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      {promo.is_active ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Active
                        </span>
                      ) : (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <XCircle className="w-3 h-3" /> Inactive
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-[#57534E] mb-3">{promo.description || 'No description'}</p>
                    
                    <div className="flex flex-wrap gap-4 text-xs text-[#57534E]">
                      <span className="flex items-center gap-1">
                        <Percent className="w-3 h-3" />
                        {promo.discount_percent}% off
                      </span>
                      <span className="flex items-center gap-1">
                        <Gift className="w-3 h-3" />
                        {promo.tier_granted === 'tier1' ? 'Essential' : 'Premium'} for {promo.duration_days} days
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {promo.current_uses}/{promo.max_uses} used
                      </span>
                      {promo.expires_at && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Expires: {formatLocalDate(promo.expires_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {promo.is_active && (
                    <Button
                      onClick={() => handleDeleteCode(promo.code)}
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Redemptions List */}
      {activeTab === 'redemptions' && (
        <div className="grid gap-4">
          {redemptions.length === 0 ? (
            <Card className="p-8 text-center">
              <Gift className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-[#57534E]">No redemptions yet.</p>
            </Card>
          ) : (
            redemptions.map((redemption, i) => (
              <Card key={i} className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-mono font-bold text-primary">{redemption.code}</span>
                      <span className="text-xs bg-stone-100 px-2 py-0.5 rounded">
                        {redemption.tier_granted === 'tier1' ? 'Essential' : 'Premium'}
                      </span>
                    </div>
                    <p className="text-sm text-[#57534E]">{redemption.user_email}</p>
                  </div>
                  <div className="text-right text-sm text-[#57534E]">
                    <p className="flex items-center gap-1 justify-end">
                      <Clock className="w-3 h-3" />
                      {formatLocalDate(redemption.redeemed_at)}
                    </p>
                    <p className="text-xs">{redemption.duration_days} days granted</p>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {activeTab === 'teacher-funnel' && (
        <div className="space-y-4" data-testid="teacher-funnel-panel">
          {!teacherFunnel ? (
            <Card className="p-8 text-center">
              <GraduationCap className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-[#57534E]">Loading teacher activation funnel…</p>
            </Card>
          ) : (
            <>
              {/* Funnel visualization */}
              <Card className="p-6">
                <h3 className="text-lg font-nunito font-bold text-[#2D2D2D] mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" /> Activation Funnel
                </h3>
                <div className="space-y-3">
                  {(() => {
                    const s = teacherFunnel.summary;
                    const max = Math.max(s.total_invites, 1);
                    const steps = [
                      { label: 'Invites Created', count: s.total_invites, color: 'bg-blue-500', icon: Users },
                      { label: 'Emails Sent', count: s.invites_emailed, color: 'bg-purple-500', icon: Mail, sub: `${s.total_emails_sent} total emails dispatched` },
                      { label: 'Teachers Activated', count: s.invites_activated, color: 'bg-green-500', icon: CheckCircle },
                      { label: 'Reports Submitted', count: s.total_teacher_reports, color: 'bg-amber-500', icon: ClipboardList, sub: `${s.reports_from_active_links} from active links` },
                    ];
                    return steps.map((step) => {
                      const Icon = step.icon;
                      const widthPct = (step.count / max) * 100;
                      return (
                        <div key={step.label}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-[#2D2D2D] flex items-center gap-2">
                              <Icon className="w-4 h-4 text-[#57534E]" />
                              {step.label}
                            </span>
                            <span className="text-sm font-bold text-[#2D2D2D]">{step.count}</span>
                          </div>
                          <div className="w-full bg-stone-100 rounded-full h-3 overflow-hidden">
                            <div
                              className={`h-3 ${step.color} transition-all`}
                              style={{ width: `${Math.max(widthPct, 2)}%` }}
                            />
                          </div>
                          {step.sub && <p className="text-xs text-stone-500 mt-1">{step.sub}</p>}
                        </div>
                      );
                    });
                  })()}
                </div>
              </Card>

              {/* Conversion rates */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <Mail className="w-5 h-5 text-blue-600 mb-1" />
                  <p className="text-2xl font-bold text-blue-700">{teacherFunnel.rates.email_send_rate}%</p>
                  <p className="text-xs text-blue-700">of invites get an email</p>
                </Card>
                <Card className="p-4 bg-green-50 border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-600 mb-1" />
                  <p className="text-2xl font-bold text-green-700">{teacherFunnel.rates.overall_activation_rate}%</p>
                  <p className="text-xs text-green-700">overall activation rate</p>
                </Card>
                <Card className="p-4 bg-purple-50 border-purple-200">
                  <Send className="w-5 h-5 text-purple-600 mb-1" />
                  <p className="text-2xl font-bold text-purple-700">{teacherFunnel.rates.emailed_activation_rate}%</p>
                  <p className="text-xs text-purple-700">emailed → activated</p>
                </Card>
                <Card className="p-4 bg-stone-50 border-stone-200">
                  <Users className="w-5 h-5 text-stone-600 mb-1" />
                  <p className="text-2xl font-bold text-stone-700">{teacherFunnel.rates.non_emailed_activation_rate}%</p>
                  <p className="text-xs text-stone-700">non-emailed → activated</p>
                </Card>
              </div>

              {/* Uplift callout */}
              <Card className={`p-5 ${teacherFunnel.uplift.emailed_vs_non_emailed_pp >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-start gap-3">
                  <TrendingUp className={`w-6 h-6 flex-shrink-0 ${teacherFunnel.uplift.emailed_vs_non_emailed_pp >= 0 ? 'text-emerald-600' : 'text-amber-600'}`} />
                  <div>
                    <p className="text-sm font-semibold text-[#2D2D2D]">
                      Email uplift: <span className={teacherFunnel.uplift.emailed_vs_non_emailed_pp >= 0 ? 'text-emerald-700' : 'text-amber-700'}>
                        {teacherFunnel.uplift.emailed_vs_non_emailed_pp >= 0 ? '+' : ''}{teacherFunnel.uplift.emailed_vs_non_emailed_pp} pp
                      </span>
                    </p>
                    <p className="text-xs text-[#57534E] mt-1">{teacherFunnel.uplift.note}</p>
                  </div>
                </div>
              </Card>

              {/* Breakdown */}
              <Card className="p-5">
                <h4 className="text-sm font-semibold text-[#2D2D2D] mb-3">Status Breakdown</h4>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-xl font-bold text-amber-600">{teacherFunnel.summary.invites_pending}</p>
                    <p className="text-xs text-stone-500">Pending</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-green-600">{teacherFunnel.summary.invites_activated}</p>
                    <p className="text-xs text-stone-500">Active</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-red-600">{teacherFunnel.summary.invites_revoked}</p>
                    <p className="text-xs text-stone-500">Revoked</p>
                  </div>
                </div>
                <p className="text-xs text-stone-400 mt-4 text-center">
                  Refreshed at {new Date(teacherFunnel.generated_at).toLocaleString()}
                </p>
              </Card>
            </>
          )}
        </div>
      )}

      {activeTab === 'news' && (
        <div className="space-y-4" data-testid="news-admin-panel">
          {editingPost ? (
            <Card className="p-6">
              <h3 className="text-lg font-nunito font-bold text-[#2D2D2D] mb-4">
                {editingPost === 'new' ? 'New News Post' : 'Edit Post'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-stone-600">Title</label>
                  <input
                    type="text"
                    value={postForm.title}
                    onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                    className="w-full mt-1 p-2 border rounded-lg"
                    placeholder="e.g., Teacher Observer is now live!"
                    maxLength={200}
                    data-testid="news-form-title"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-stone-600">
                    Body <span className="text-stone-400">(supports **bold**, *italic*, and line breaks)</span>
                  </label>
                  <textarea
                    value={postForm.body}
                    onChange={(e) => setPostForm({ ...postForm, body: e.target.value })}
                    className="w-full mt-1 p-2 border rounded-lg h-40 font-mono text-sm"
                    placeholder="Tell parents what's new..."
                    maxLength={10000}
                    data-testid="news-form-body"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-stone-600">Category</label>
                    <select
                      value={postForm.category}
                      onChange={(e) => setPostForm({ ...postForm, category: e.target.value })}
                      className="w-full mt-1 p-2 border rounded-lg"
                      data-testid="news-form-category"
                    >
                      <option value="new_feature">✨ New Feature</option>
                      <option value="medical_resource">🩺 Medical Resource</option>
                      <option value="community">📣 Community</option>
                      <option value="bug_fix">🐛 Bug Fix</option>
                      <option value="awareness">❤️ Awareness</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer mt-6">
                    <input
                      type="checkbox"
                      checked={postForm.is_published}
                      onChange={(e) => setPostForm({ ...postForm, is_published: e.target.checked })}
                      data-testid="news-form-published"
                    />
                    <span className="text-sm">Published</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer mt-6">
                    <input
                      type="checkbox"
                      checked={postForm.is_pinned}
                      onChange={(e) => setPostForm({ ...postForm, is_pinned: e.target.checked })}
                      data-testid="news-form-pinned"
                    />
                    <span className="text-sm flex items-center gap-1"><Pin className="w-3 h-3" /> Pin to top</span>
                  </label>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={savePost} data-testid="news-form-save">
                    {editingPost === 'new' ? 'Create Post' : 'Save Changes'}
                  </Button>
                  <Button variant="outline" onClick={() => setEditingPost(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <p className="text-sm text-stone-600">
                  {newsPosts.length} post{newsPosts.length === 1 ? '' : 's'} total · visible to logged-in users at <code>/news</code> and the 3 most recent on the landing page
                </p>
                <Button onClick={startNewPost} data-testid="new-post-btn">
                  <Plus className="w-4 h-4 mr-2" /> New Post
                </Button>
              </div>

              {newsPosts.length === 0 ? (
                <Card className="p-8 text-center">
                  <Newspaper className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-[#57534E]">No posts yet — click "New Post" to publish your first update.</p>
                </Card>
              ) : (
                <div className="space-y-2">
                  {newsPosts.map((p) => (
                    <Card key={p.id} className="p-4 flex items-start justify-between gap-3" data-testid={`admin-news-row-${p.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">
                            {p.category.replace('_', ' ')}
                          </span>
                          {p.is_pinned && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                              <Pin className="w-3 h-3" /> Pinned
                            </span>
                          )}
                          {!p.is_published && (
                            <span className="text-xs bg-stone-200 text-stone-600 px-1.5 py-0.5 rounded">Draft</span>
                          )}
                          <span className="text-xs text-stone-400">
                            {formatLocalDate(p.published_at || p.created_at)}
                          </span>
                        </div>
                        <p className="font-semibold text-[#2D2D2D] truncate">{p.title}</p>
                        <p className="text-xs text-stone-500 line-clamp-1">{p.body}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => startEditPost(p)} data-testid={`edit-post-${p.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deletePost(p.id)} className="text-red-600 hover:bg-red-50" data-testid={`delete-post-${p.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Luna Waitlist Tab */}
      {activeTab === 'luna-waitlist' && (
        <div className="space-y-4" data-testid="luna-waitlist-tab-content">
          <Card className="p-5 bg-gradient-to-br from-rose-50 via-pink-50 to-violet-50 border-rose-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-400 to-violet-500 flex items-center justify-center shadow-md">
                  <Moon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#2D2D2D]">Luna Pathways Waitlist</h3>
                  <p className="text-sm text-[#57534E]">
                    {lunaWaitlist.length} {lunaWaitlist.length === 1 ? 'person is' : 'people are'} waiting for launch
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyAllLunaEmails}
                  className="border-violet-300 text-violet-700 hover:bg-violet-50"
                  data-testid="copy-luna-emails-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All Emails
                </Button>
                <Button
                  size="sm"
                  onClick={exportLunaCsv}
                  variant="outline"
                  className="border-violet-300 text-violet-700 hover:bg-violet-50"
                  data-testid="export-luna-csv-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button
                  size="sm"
                  onClick={openNotifyDialog}
                  className="bg-gradient-to-r from-rose-500 to-violet-500 text-white hover:from-rose-600 hover:to-violet-600 shadow-md"
                  data-testid="notify-all-luna-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Notify All
                </Button>
              </div>
            </div>
          </Card>

          {lunaWaitlist.length > 0 && (
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search by email or name…"
                value={lunaSearch}
                onChange={(e) => setLunaSearch(e.target.value)}
                className="max-w-sm"
                data-testid="luna-search-input"
              />
              {lunaSearch && (
                <Button variant="ghost" size="sm" onClick={() => setLunaSearch('')}>
                  Clear
                </Button>
              )}
            </div>
          )}

          {lunaWaitlist.length === 0 ? (
            <Card className="p-12 text-center">
              <Moon className="w-12 h-12 text-rose-300 mx-auto mb-4" />
              <p className="text-[#57534E] font-medium">No signups yet</p>
              <p className="text-sm text-stone-500 mt-1">
                When visitors join the waitlist on the landing page, they'll appear here.
              </p>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-[#57534E]">Email</th>
                      <th className="text-left px-4 py-3 font-semibold text-[#57534E]">Name</th>
                      <th className="text-left px-4 py-3 font-semibold text-[#57534E]">Source</th>
                      <th className="text-left px-4 py-3 font-semibold text-[#57534E]">Signed Up</th>
                      <th className="text-right px-4 py-3 font-semibold text-[#57534E]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lunaWaitlist
                      .filter(entry => {
                        if (!lunaSearch.trim()) return true;
                        const q = lunaSearch.toLowerCase();
                        return (
                          (entry.email || '').toLowerCase().includes(q) ||
                          (entry.name || '').toLowerCase().includes(q)
                        );
                      })
                      .map((entry) => (
                        <tr key={entry.id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`luna-entry-${entry.id}`}>
                          <td className="px-4 py-3 font-medium text-[#2D2D2D]">
                            <a href={`mailto:${entry.email}`} className="hover:text-primary hover:underline">
                              {entry.email}
                            </a>
                          </td>
                          <td className="px-4 py-3 text-[#57534E]">{entry.name || <span className="text-stone-400">—</span>}</td>
                          <td className="px-4 py-3">
                            <span className="text-xs bg-stone-100 px-2 py-1 rounded text-[#57534E]">
                              {entry.source || 'unknown'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[#57534E]">
                            {entry.created_at
                              ? formatLocalDate(entry.created_at, 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {entry.notified ? (
                              <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
                                <CheckCircle className="w-3 h-3" /> Notified
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                                <Clock className="w-3 h-3" /> Pending
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Notify All Modal */}
          {lunaNotifyOpen && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="luna-notify-modal">
              <Card className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-400 to-violet-500 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">Send Launch Email</h2>
                    <p className="text-sm text-[#57534E]">
                      {lunaOnlyPending
                        ? `${lunaWaitlist.filter(e => !e.notified).length} pending recipient(s)`
                        : `${lunaWaitlist.length} total recipient(s)`}
                    </p>
                  </div>
                </div>

                {lunaSendResult ? (
                  <Card className="p-5 bg-emerald-50 border-emerald-200 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      <h3 className="font-bold text-emerald-900">
                        {lunaSendResult.dry_run ? 'Dry Run Complete' : 'Send Complete'}
                      </h3>
                    </div>
                    <div className="text-sm text-emerald-800 space-y-1">
                      <p>Total recipients: <strong>{lunaSendResult.total}</strong></p>
                      {!lunaSendResult.dry_run && (
                        <>
                          <p>Sent successfully: <strong>{lunaSendResult.sent}</strong></p>
                          {lunaSendResult.failed > 0 && (
                            <p className="text-amber-700">
                              Failed: <strong>{lunaSendResult.failed}</strong>
                              {lunaSendResult.failed_emails && lunaSendResult.failed_emails.length > 0 && (
                                <span className="block text-xs mt-1">
                                  {lunaSendResult.failed_emails.join(', ')}
                                </span>
                              )}
                            </p>
                          )}
                        </>
                      )}
                      {lunaSendResult.dry_run && lunaSendResult.recipient_preview && (
                        <p className="text-xs">
                          Preview: {lunaSendResult.recipient_preview.join(', ')}
                          {lunaSendResult.total > lunaSendResult.recipient_preview.length && ' …'}
                        </p>
                      )}
                    </div>
                  </Card>
                ) : null}

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[#2D2D2D] block mb-1">Email Subject</label>
                    <Input
                      value={lunaEmailSubject}
                      onChange={(e) => setLunaEmailSubject(e.target.value)}
                      placeholder="Luna Pathways is live — your waitlist invite"
                      data-testid="luna-email-subject"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#2D2D2D] block mb-1">
                      Email Body (HTML)
                    </label>
                    <Textarea
                      value={lunaEmailHtml}
                      onChange={(e) => setLunaEmailHtml(e.target.value)}
                      rows={10}
                      className="font-mono text-xs"
                      placeholder="<html>…</html>"
                      data-testid="luna-email-html"
                    />
                    <p className="text-xs text-stone-500 mt-1">
                      Default Luna-branded template loaded. Edit as needed.
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="luna-only-pending"
                      checked={lunaOnlyPending}
                      onChange={(e) => setLunaOnlyPending(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor="luna-only-pending" className="text-sm text-[#57534E]">
                      Skip already-notified recipients (recommended)
                    </label>
                  </div>

                  <Card className="p-3 bg-amber-50 border-amber-200">
                    <p className="text-xs text-amber-900 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Heads up:</strong> Clicking "Send to All" will email every recipient
                        immediately via Resend. Use "Dry Run" first to verify the count.
                      </span>
                    </p>
                  </Card>
                </div>

                <div className="flex gap-3 pt-5 flex-wrap">
                  <Button
                    variant="outline"
                    onClick={() => { setLunaNotifyOpen(false); setLunaSendResult(null); }}
                    className="flex-1"
                    disabled={lunaSending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => sendLunaNotification(true)}
                    disabled={lunaSending}
                    className="flex-1 border-violet-300 text-violet-700 hover:bg-violet-50"
                    data-testid="luna-dry-run-btn"
                  >
                    {lunaSending ? 'Running…' : 'Dry Run'}
                  </Button>
                  <Button
                    onClick={() => sendLunaNotification(false)}
                    disabled={lunaSending}
                    className="flex-1 bg-gradient-to-r from-rose-500 to-violet-500 text-white hover:from-rose-600 hover:to-violet-600"
                    data-testid="luna-send-all-btn"
                  >
                    {lunaSending ? 'Sending…' : 'Send to All'}
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* Apple IAP Subscriptions Tab */}
      {activeTab === 'apple-iap' && (
        <div className="space-y-4" data-testid="apple-iap-tab-content">
          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: 'active', label: 'Active', color: 'emerald', count: iapData?.counts?.active ?? 0 },
              { key: 'in_trial', label: 'In Trial', color: 'sky', count: iapData?.counts?.in_trial ?? 0 },
              { key: 'cancelled', label: 'Cancelled', color: 'amber', count: iapData?.counts?.cancelled ?? 0 },
              { key: 'expired', label: 'Expired', color: 'stone', count: iapData?.counts?.expired ?? 0 },
            ].map((stat) => (
              <Card key={stat.key} className={`p-4 bg-${stat.color}-50 border-${stat.color}-200`} data-testid={`iap-stat-${stat.key}`}>
                <p className={`text-xs font-semibold text-${stat.color}-700 uppercase tracking-wide`}>
                  Total {stat.label}
                </p>
                <p className={`text-3xl font-bold text-${stat.color}-900 mt-1`}>{stat.count}</p>
              </Card>
            ))}
          </div>

          {/* Filters */}
          <Card className="p-4 bg-stone-50 border-stone-200">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-semibold text-[#57534E] block mb-1">Search</label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Email, name, product ID, or transaction ID…"
                    value={iapSearch}
                    onChange={(e) => setIapSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') fetchIapSubscriptions(); }}
                    data-testid="iap-search-input"
                  />
                  <Button onClick={fetchIapSubscriptions} size="sm" variant="outline">
                    Go
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#57534E] block mb-1">Status</label>
                <select
                  value={iapStatusFilter}
                  onChange={(e) => setIapStatusFilter(e.target.value)}
                  className="h-9 px-3 rounded-md border border-stone-200 bg-white text-sm"
                  data-testid="iap-status-filter"
                >
                  <option value="all">All statuses</option>
                  <option value="active">Active</option>
                  <option value="in_trial">In Trial</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="expired">Expired</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#57534E] block mb-1">From</label>
                <Input
                  type="date"
                  value={iapDateFrom}
                  onChange={(e) => setIapDateFrom(e.target.value)}
                  className="h-9 w-[150px]"
                  data-testid="iap-date-from"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#57534E] block mb-1">To</label>
                <Input
                  type="date"
                  value={iapDateTo}
                  onChange={(e) => setIapDateTo(e.target.value)}
                  className="h-9 w-[150px]"
                  data-testid="iap-date-to"
                />
              </div>
              {(iapSearch || iapStatusFilter !== 'all' || iapDateFrom || iapDateTo) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIapSearch(''); setIapStatusFilter('all'); setIapDateFrom(''); setIapDateTo('');
                    setTimeout(fetchIapSubscriptions, 0);
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          </Card>

          {/* Table */}
          {iapLoading ? (
            <Card className="p-12 text-center">
              <p className="text-[#57534E]">Loading…</p>
            </Card>
          ) : !iapData || iapData.subscriptions.length === 0 ? (
            <Card className="p-12 text-center">
              <Smartphone className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-[#57534E] font-medium">No Apple subscriptions yet</p>
              <p className="text-sm text-stone-500 mt-1">
                When users purchase via iOS App Store, their subscriptions will appear here.
              </p>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-[#57534E]">User</th>
                      <th className="text-left px-4 py-3 font-semibold text-[#57534E]">Product</th>
                      <th className="text-left px-4 py-3 font-semibold text-[#57534E]">Status</th>
                      <th className="text-left px-4 py-3 font-semibold text-[#57534E]">Renews / Expires</th>
                      <th className="text-left px-4 py-3 font-semibold text-[#57534E]">Last Updated</th>
                      <th className="text-left px-4 py-3 font-semibold text-[#57534E]">Transaction</th>
                    </tr>
                  </thead>
                  <tbody>
                    {iapData.subscriptions.map((sub) => {
                      const statusColors = {
                        active: 'bg-emerald-100 text-emerald-700',
                        in_trial: 'bg-sky-100 text-sky-700',
                        cancelled: 'bg-amber-100 text-amber-700',
                        expired: 'bg-stone-200 text-stone-700',
                        failed: 'bg-red-100 text-red-700',
                        refunded: 'bg-purple-100 text-purple-700',
                      };
                      return (
                        <tr
                          key={`${sub.user_id}-${sub.original_transaction_id}`}
                          className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer"
                          onClick={() => openIapDetail(sub.user_id, sub.user_email)}
                          data-testid={`iap-row-${sub.original_transaction_id}`}
                        >
                          <td className="px-4 py-3">
                            <div className="font-medium text-[#2D2D2D]">{sub.user_email || '—'}</div>
                            {sub.user_name && <div className="text-xs text-stone-500">{sub.user_name}</div>}
                          </td>
                          <td className="px-4 py-3 text-[#57534E] font-mono text-xs">
                            {sub.product_id}
                            {sub.environment === 'sandbox' && (
                              <span className="ml-2 text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">sandbox</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusColors[sub.status] || 'bg-stone-100 text-stone-700'}`}>
                              {sub.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[#57534E]">
                            {sub.expires_date
                              ? formatLocalDate(sub.expires_date, 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-[#57534E] text-xs">
                            {sub.updated_at
                              ? new Date(sub.updated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-xs text-stone-500 font-mono">
                            {sub.original_transaction_id?.slice(0, 14)}…
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Detail modal */}
          {iapDetailUser && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="iap-detail-modal">
              <Card className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">Subscription History</h2>
                    <p className="text-sm text-[#57534E]">{iapDetailUser.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={refreshIapFromApple}
                      disabled={iapRefreshing || !iapDetailHistory}
                      size="sm"
                      variant="outline"
                      className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      data-testid="refresh-from-apple-btn"
                      title="Re-fetch this user's transaction history from Apple's App Store Server API"
                    >
                      {iapRefreshing ? 'Refreshing…' : 'Refresh from Apple'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setIapDetailUser(null); setIapDetailHistory(null); }}>
                      ✕
                    </Button>
                  </div>
                </div>
                {!iapDetailHistory ? (
                  <p className="text-[#57534E]">Loading history…</p>
                ) : iapDetailHistory.history.length === 0 ? (
                  <p className="text-[#57534E]">No Apple subscription records found.</p>
                ) : (
                  <div className="space-y-3">
                    {iapDetailHistory.history.map((sub) => {
                      const statusColors = {
                        active: 'bg-emerald-100 text-emerald-700',
                        in_trial: 'bg-sky-100 text-sky-700',
                        cancelled: 'bg-amber-100 text-amber-700',
                        expired: 'bg-stone-200 text-stone-700',
                        failed: 'bg-red-100 text-red-700',
                        refunded: 'bg-purple-100 text-purple-700',
                      };
                      return (
                        <Card key={sub.original_transaction_id} className="p-4 border-stone-200">
                          <div className="flex justify-between items-start gap-3 mb-2">
                            <div>
                              <p className="font-semibold text-[#2D2D2D] text-sm font-mono">{sub.product_id}</p>
                              <p className="text-xs text-stone-500">Tier: {sub.tier} · {sub.environment}</p>
                            </div>
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusColors[sub.status]}`}>
                              {sub.status}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-xs text-[#57534E]">
                            <div>
                              <span className="font-semibold">Expires:</span>{' '}
                              {sub.expires_date ? new Date(sub.expires_date).toLocaleString() : '—'}
                            </div>
                            <div>
                              <span className="font-semibold">Updated:</span>{' '}
                              {sub.updated_at ? new Date(sub.updated_at).toLocaleString() : '—'}
                            </div>
                            <div className="col-span-2 font-mono text-xs">
                              <span className="font-semibold">Original Txn:</span> {sub.original_transaction_id}
                            </div>
                            <div className="col-span-2 font-mono text-xs">
                              <span className="font-semibold">Latest Txn:</span> {sub.transaction_id}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </Card>
            </div>
          )}
        </div>
      )}

      {/* ==================== LEGAL AUDIT TAB ==================== */}
      {activeTab === 'legal-audit' && (
        <div className="space-y-4" data-testid="legal-audit-tab-content">
          <Card className="p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-stone-700" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-nunito font-bold text-[#2D2D2D]">Legal Audit Export</h2>
                <p className="text-sm text-[#57534E]">
                  Exports a CSV of every first-sign-in disclaimer acceptance — useful for App Store
                  reviewers, SOC-2 / HIPAA-readiness audits, or any &ldquo;did the user actually agree?&rdquo; question.
                </p>
                <p className="text-xs text-stone-500 mt-1">
                  Columns: <code>user_email, disclaimer_type, version, accepted_at, user_agent, platform</code>
                </p>
              </div>
            </div>

            {/* Summary tiles */}
            {legalLoading && !legalSummary ? (
              <div className="text-sm text-[#57534E]">Loading audit summary…</div>
            ) : legalSummary ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Total Acceptances</p>
                  <p className="text-2xl font-bold text-[#2D2D2D]">{legalSummary.total_acceptances}</p>
                </div>
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Distinct Users</p>
                  <p className="text-2xl font-bold text-[#2D2D2D]">{legalSummary.distinct_users}</p>
                </div>
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Medical</p>
                  <p className="text-2xl font-bold text-[#2D2D2D]">{legalSummary.by_type?.medical || 0}</p>
                  <p className="text-[10px] text-stone-400">v{legalSummary.current_versions?.medical}</p>
                </div>
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">AI Wellness</p>
                  <p className="text-2xl font-bold text-[#2D2D2D]">{legalSummary.by_type?.ai_wellness || 0}</p>
                  <p className="text-[10px] text-stone-400">v{legalSummary.current_versions?.ai_wellness}</p>
                </div>
                <div className="rounded-lg border border-stone-200 p-3 col-span-2 md:col-span-1">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Privacy</p>
                  <p className="text-2xl font-bold text-[#2D2D2D]">
                    {legalSummary.by_type?.privacy_responsibility || 0}
                  </p>
                  <p className="text-[10px] text-stone-400">v{legalSummary.current_versions?.privacy_responsibility}</p>
                </div>
              </div>
            ) : null}

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="text-xs font-medium text-[#2D2D2D]">Start date</label>
                <Input
                  type="date"
                  value={legalStartDate}
                  onChange={(e) => setLegalStartDate(e.target.value)}
                  className="mt-1"
                  data-testid="legal-audit-start-date"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#2D2D2D]">End date</label>
                <Input
                  type="date"
                  value={legalEndDate}
                  onChange={(e) => setLegalEndDate(e.target.value)}
                  className="mt-1"
                  data-testid="legal-audit-end-date"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-[#2D2D2D]">User email contains</label>
                <Input
                  type="text"
                  placeholder="e.g. parent@example.com"
                  value={legalEmailFilter}
                  onChange={(e) => setLegalEmailFilter(e.target.value)}
                  className="mt-1"
                  data-testid="legal-audit-email-filter"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={downloadLegalAuditCsv}
                disabled={legalExporting}
                className="bg-primary text-white"
                data-testid="legal-audit-export-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {legalExporting ? 'Exporting…' : 'Legal Audit Export (CSV)'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setLegalStartDate('');
                  setLegalEndDate('');
                  setLegalEmailFilter('');
                }}
                data-testid="legal-audit-clear-filters-btn"
              >
                Clear Filters
              </Button>
              <Button
                variant="outline"
                onClick={fetchLegalSummary}
                disabled={legalLoading}
                data-testid="legal-audit-refresh-btn"
              >
                {legalLoading ? 'Refreshing…' : 'Refresh Summary'}
              </Button>
            </div>
          </Card>

          {/* Recent acceptances */}
          {legalSummary && legalSummary.recent && legalSummary.recent.length > 0 && (
            <Card className="p-0 overflow-hidden">
              <div className="px-6 py-3 border-b border-stone-200 bg-stone-50">
                <p className="text-sm font-semibold text-[#2D2D2D]">Recent Acceptances (25)</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
                    <tr>
                      <th className="px-4 py-2">User Email</th>
                      <th className="px-4 py-2">Type</th>
                      <th className="px-4 py-2">Version</th>
                      <th className="px-4 py-2">Accepted At (UTC)</th>
                      <th className="px-4 py-2">Platform</th>
                    </tr>
                  </thead>
                  <tbody>
                    {legalSummary.recent.map((row) => (
                      <tr key={row.id} className="border-t border-stone-100">
                        <td className="px-4 py-2 text-[#2D2D2D]">{row.user_email}</td>
                        <td className="px-4 py-2 capitalize">{(row.disclaimer_type || '').replace('_', ' ')}</td>
                        <td className="px-4 py-2 font-mono">{row.version}</td>
                        <td className="px-4 py-2 font-mono text-xs">{row.accepted_at}</td>
                        <td className="px-4 py-2">{row.platform}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ==================== ACTIVATION FUNNEL TAB ==================== */}
      {activeTab === 'activation-funnel' && (
        <div className="space-y-4" data-testid="activation-funnel-tab-content">
          <Card className="p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-indigo-700" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-nunito font-bold text-[#2D2D2D]">Activation Funnel</h2>
                <p className="text-sm text-[#57534E]">
                  Signed up → Welcome delivered → Opened → Clicked CTA → Logged first symptom.
                  Where the bar shrinks the most is your conversion graveyard.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={funnelDays}
                  onChange={(e) => setFunnelDays(parseInt(e.target.value, 10))}
                  className="px-3 py-2 border border-stone-200 rounded-md text-sm"
                  data-testid="funnel-window-select"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={365}>Last 365 days</option>
                </select>
                <Button
                  variant="outline"
                  onClick={fetchActivationFunnel}
                  disabled={funnelLoading}
                  data-testid="funnel-refresh-btn"
                >
                  {funnelLoading ? 'Loading…' : 'Refresh'}
                </Button>
              </div>
            </div>

            {!funnelData ? (
              <div className="text-sm text-[#57534E]">Loading funnel…</div>
            ) : (
              <>
                {/* KPI tiles */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
                  {funnelData.stages.map((s) => (
                    <div key={s.key} className="rounded-lg border border-stone-200 p-3">
                      <p className="text-[10px] text-stone-500 uppercase tracking-wider">{s.label}</p>
                      <p className="text-2xl font-bold text-[#2D2D2D]">{s.count}</p>
                      <p className="text-[10px] text-stone-400">{s.pct_of_top}% of signed up</p>
                    </div>
                  ))}
                </div>

                {/* Stacked / sequential bar chart */}
                <div className="w-full h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={funnelData.stages}
                      margin={{ top: 10, right: 16, left: 0, bottom: 6 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE9" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#57534E' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#57534E' }} allowDecimals={false} />
                      <Tooltip
                        formatter={(value, _name, item) => [
                          `${value} (${item?.payload?.pct_of_top || 0}% of signed up)`,
                          item?.payload?.label || '',
                        ]}
                        contentStyle={{ borderRadius: 8, border: '1px solid #E7E5E4' }}
                      />
                      <Bar dataKey="count" fill="#6B9B8C" radius={[6, 6, 0, 0]}>
                        <LabelList dataKey="count" position="top" style={{ fontSize: 11, fill: '#2D2D2D' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Step-by-step drop-off table */}
                <div className="mt-5 overflow-x-auto">
                  <table className="w-full text-sm border border-stone-200 rounded-lg overflow-hidden">
                    <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
                      <tr>
                        <th className="px-4 py-2">Step</th>
                        <th className="px-4 py-2 text-right">Kept</th>
                        <th className="px-4 py-2 text-right">Dropped</th>
                      </tr>
                    </thead>
                    <tbody>
                      {funnelData.drops.map((d, i) => (
                        <tr key={i} className="border-t border-stone-100">
                          <td className="px-4 py-2 text-[#2D2D2D]">
                            <span className="text-stone-500">{d.from}</span>
                            <span className="mx-1.5 text-stone-300">→</span>
                            <span className="font-medium">{d.to}</span>
                          </td>
                          <td className="px-4 py-2 text-right text-emerald-700 font-medium">
                            {d.kept_pct}%
                          </td>
                          <td className="px-4 py-2 text-right text-rose-700 font-medium">
                            {d.drop_count} ({d.drop_pct}%)
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {funnelData.bounced > 0 && (
                  <div className="mt-4 px-4 py-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800">
                    <strong>{funnelData.bounced}</strong> welcome email{funnelData.bounced === 1 ? '' : 's'} bounced
                    in this window. Likely typo'd addresses — consider an in-app banner asking those users to confirm their email.
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      )}

      {/* ==================== SUBSCRIPTIONS TAB ==================== */}
      {activeTab === 'subscriptions' && (
        <div className="space-y-4" data-testid="subscriptions-tab-content">
          <Card className="p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Crown className="w-5 h-5 text-amber-700" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-nunito font-bold text-[#2D2D2D]">Subscriptions</h2>
                <p className="text-sm text-[#57534E]">
                  Single source of truth — everyone with active Tier 1 or Tier 2 access,
                  regardless of whether they got it via Stripe, Apple, or a manual grant.
                </p>
              </div>
            </div>

            {/* KPI tiles */}
            {subsData && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Active</p>
                  <p className="text-2xl font-bold text-emerald-700">{subsData.totals.active}</p>
                </div>
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Tier 1</p>
                  <p className="text-2xl font-bold text-[#2D2D2D]">{subsData.totals.tier1}</p>
                </div>
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Tier 2</p>
                  <p className="text-2xl font-bold text-[#2D2D2D]">{subsData.totals.tier2}</p>
                </div>
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Stripe</p>
                  <p className="text-2xl font-bold text-[#2D2D2D]">{subsData.totals.by_source.stripe}</p>
                </div>
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Apple</p>
                  <p className="text-2xl font-bold text-[#2D2D2D]">{subsData.totals.by_source.apple}</p>
                </div>
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Manual Grant</p>
                  <p className="text-2xl font-bold text-[#2D2D2D]">{subsData.totals.by_source.manual_grant}</p>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="text-xs font-medium text-[#2D2D2D]">Status</label>
                <select
                  value={subsStatus}
                  onChange={(e) => setSubsStatus(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-stone-200 rounded-md text-sm"
                  data-testid="subs-status-filter"
                >
                  <option value="active">Active only</option>
                  <option value="expired">Expired only</option>
                  <option value="all">All</option>
                  <option value="tier1">Tier 1 only</option>
                  <option value="tier2">Tier 2 only</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#2D2D2D]">Source</label>
                <select
                  value={subsSource}
                  onChange={(e) => setSubsSource(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-stone-200 rounded-md text-sm"
                  data-testid="subs-source-filter"
                >
                  <option value="all">All sources</option>
                  <option value="stripe">Stripe</option>
                  <option value="apple">Apple</option>
                  <option value="manual_grant">Manual grant</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-[#2D2D2D]">Search email</label>
                <Input
                  type="text"
                  placeholder="e.g. holly@example.com"
                  value={subsSearch}
                  onChange={(e) => setSubsSearch(e.target.value)}
                  className="mt-1"
                  data-testid="subs-search-input"
                />
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              <Button
                variant="outline"
                onClick={() => { setSubsStatus('active'); setSubsSource('all'); setSubsSearch(''); }}
                data-testid="subs-clear-filters"
              >
                Clear Filters
              </Button>
              <Button
                variant="outline"
                onClick={fetchSubscriptions}
                disabled={subsLoading}
                data-testid="subs-refresh-btn"
              >
                {subsLoading ? 'Refreshing…' : 'Refresh'}
              </Button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto border border-stone-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="px-4 py-2">User</th>
                    <th className="px-4 py-2">Tier</th>
                    <th className="px-4 py-2">Source</th>
                    <th className="px-4 py-2">Start</th>
                    <th className="px-4 py-2">End</th>
                    <th className="px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {!subsData || subsData.subscriptions.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-stone-500">
                        {subsLoading ? 'Loading…' : 'No subscriptions match these filters.'}
                      </td>
                    </tr>
                  ) : (
                    subsData.subscriptions.map((s) => (
                      <tr key={s.user_id} className="border-t border-stone-100">
                        <td className="px-4 py-2">
                          <div className="text-[#2D2D2D]">{s.name || '(no name)'}</div>
                          <div className="text-xs text-stone-500">{s.email}</div>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            s.tier === 'tier2'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-sky-100 text-sky-800'
                          }`}>
                            {s.tier === 'tier2' ? 'Tier 2' : 'Tier 1'}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            s.source === 'stripe'        ? 'bg-violet-100 text-violet-800' :
                            s.source === 'apple'         ? 'bg-stone-200 text-stone-800' :
                            s.source === 'manual_grant'  ? 'bg-emerald-100 text-emerald-800' :
                                                           'bg-stone-100 text-stone-600'
                          }`}>
                            {s.source === 'manual_grant' ? 'Manual' : s.source}
                          </span>
                          {s.granted_by && (
                            <div className="text-[10px] text-stone-400 mt-0.5">by {s.granted_by}</div>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs font-mono text-stone-500">
                          {s.subscription_start_date ? s.subscription_start_date.slice(0, 10) : '—'}
                        </td>
                        <td className="px-4 py-2 text-xs font-mono text-stone-500">
                          {s.subscription_end_date ? s.subscription_end_date.slice(0, 10) : '—'}
                        </td>
                        <td className="px-4 py-2">
                          {s.is_active ? (
                            <span className="text-emerald-700 font-medium">Active</span>
                          ) : (
                            <span className="text-rose-700 font-medium">Expired</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ==================== ALL TRANSACTIONS TAB ==================== */}
      {activeTab === 'all-transactions' && (
        <div className="space-y-4" data-testid="all-transactions-tab-content">
          <Card className="p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <ListTree className="w-5 h-5 text-violet-700" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-nunito font-bold text-[#2D2D2D]">All Transactions</h2>
                <p className="text-sm text-[#57534E]">
                  Unified chronological feed of every transaction event — Stripe, Apple IAP, and manual grants — in one auditable table.
                </p>
              </div>
            </div>

            {/* KPI tiles */}
            {allTxData && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Total</p>
                  <p className="text-2xl font-bold text-[#2D2D2D]">{allTxData.totals.total}</p>
                </div>
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Stripe</p>
                  <p className="text-2xl font-bold text-violet-700">{allTxData.totals.by_source.stripe}</p>
                </div>
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Apple</p>
                  <p className="text-2xl font-bold text-stone-700">{allTxData.totals.by_source.apple}</p>
                </div>
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Manual</p>
                  <p className="text-2xl font-bold text-emerald-700">{allTxData.totals.by_source.manual_grant}</p>
                </div>
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Tier 1 / 2</p>
                  <p className="text-2xl font-bold text-[#2D2D2D]">
                    {allTxData.totals.by_tier.tier1}<span className="text-sm text-stone-400"> / </span>{allTxData.totals.by_tier.tier2}
                  </p>
                </div>
                <div className="rounded-lg border border-stone-200 p-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Revenue (USD)</p>
                  <p className="text-2xl font-bold text-emerald-700">${allTxData.totals.revenue_usd.toFixed(2)}</p>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
              <div>
                <label className="text-xs font-medium text-[#2D2D2D]">Source</label>
                <select
                  value={allTxSource}
                  onChange={(e) => setAllTxSource(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-stone-200 rounded-md text-sm"
                  data-testid="all-tx-source-filter"
                >
                  <option value="all">All sources</option>
                  <option value="stripe">Stripe</option>
                  <option value="apple">Apple</option>
                  <option value="manual_grant">Manual</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#2D2D2D]">Tier</label>
                <select
                  value={allTxTier}
                  onChange={(e) => setAllTxTier(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-stone-200 rounded-md text-sm"
                  data-testid="all-tx-tier-filter"
                >
                  <option value="all">All tiers</option>
                  <option value="tier1">Tier 1</option>
                  <option value="tier2">Tier 2</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#2D2D2D]">Start date</label>
                <Input
                  type="date"
                  value={allTxStart}
                  onChange={(e) => setAllTxStart(e.target.value)}
                  className="mt-1"
                  data-testid="all-tx-start-date"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#2D2D2D]">End date</label>
                <Input
                  type="date"
                  value={allTxEnd}
                  onChange={(e) => setAllTxEnd(e.target.value)}
                  className="mt-1"
                  data-testid="all-tx-end-date"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#2D2D2D]">Search email</label>
                <Input
                  type="text"
                  placeholder="email contains…"
                  value={allTxSearch}
                  onChange={(e) => setAllTxSearch(e.target.value)}
                  className="mt-1"
                  data-testid="all-tx-search"
                />
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              <Button
                variant="outline"
                onClick={() => {
                  setAllTxSource('all'); setAllTxTier('all');
                  setAllTxSearch(''); setAllTxStart(''); setAllTxEnd('');
                }}
                data-testid="all-tx-clear-filters"
              >
                Clear Filters
              </Button>
              <Button
                variant="outline"
                onClick={fetchAllTransactions}
                disabled={allTxLoading}
                data-testid="all-tx-refresh"
              >
                {allTxLoading ? 'Refreshing…' : 'Refresh'}
              </Button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto border border-stone-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">User</th>
                    <th className="px-3 py-2">Source</th>
                    <th className="px-3 py-2">Tier</th>
                    <th className="px-3 py-2">Amount</th>
                    <th className="px-3 py-2">Plan / Product</th>
                    <th className="px-3 py-2">Transaction ID</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {!allTxData || allTxData.transactions.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-stone-500">
                        {allTxLoading ? 'Loading…' : 'No transactions match these filters.'}
                      </td>
                    </tr>
                  ) : (
                    allTxData.transactions.map((t, i) => (
                      <tr key={`${t.source}-${t.transaction_id || i}`} className="border-t border-stone-100">
                        <td className="px-3 py-2 text-xs font-mono text-stone-500 whitespace-nowrap">
                          {t.occurred_at ? t.occurred_at.slice(0, 19).replace('T', ' ') : '—'}
                        </td>
                        <td className="px-3 py-2 text-[#2D2D2D]">{t.user_email || '(no email)'}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            t.source === 'stripe'        ? 'bg-violet-100 text-violet-800' :
                            t.source === 'apple'         ? 'bg-stone-200 text-stone-800' :
                            t.source === 'manual_grant'  ? 'bg-emerald-100 text-emerald-800' :
                                                           'bg-stone-100 text-stone-600'
                          }`}>
                            {t.source === 'manual_grant' ? 'Manual' : t.source}
                          </span>
                          {t.environment === 'sandbox' && (
                            <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-800">
                              sandbox
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            t.tier === 'tier2'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-sky-100 text-sky-800'
                          }`}>
                            {t.tier === 'tier2' ? 'Tier 2' : 'Tier 1'}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {t.amount !== null && t.amount !== undefined
                            ? `$${Number(t.amount).toFixed(2)}`
                            : <span className="text-stone-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-xs text-stone-600">
                          {t.plan_id || '—'}
                          {t.interval && (
                            <div className="text-[10px] text-stone-400">{t.interval}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-[10px] text-stone-500 break-all max-w-[180px]">
                          {t.transaction_id || '—'}
                          {t.original_transaction_id && (
                            <div className="text-stone-400 mt-0.5">orig: {t.original_transaction_id}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-xs font-medium ${
                            t.status === 'active' || t.status === 'completed' ? 'text-emerald-700' :
                            t.status === 'refunded' ? 'text-rose-700' :
                            'text-stone-500'
                          }`}>
                            {t.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {allTxData && allTxData.transactions.length >= 500 && (
              <p className="text-xs text-stone-400 mt-2">
                Showing the first 500 transactions. Narrow filters to see older ones.
              </p>
            )}
          </Card>
        </div>
      )}



      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-4">Create Promo Code</h2>
            
            <form onSubmit={handleCreateCode} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#2D2D2D]">Code</label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newCode.code}
                    onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                    placeholder="SUMMER2026"
                    required
                    className="font-mono"
                  />
                  <Button type="button" variant="outline" onClick={generateRandomCode}>
                    Generate
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#2D2D2D]">Discount %</label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={newCode.discount_percent}
                    onChange={(e) => setNewCode({ ...newCode, discount_percent: parseInt(e.target.value) })}
                    required
                    className="mt-1"
                  />
                  <p className="text-xs text-[#57534E] mt-1">100% = Free access</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2D2D2D]">Max Uses</label>
                  <Input
                    type="number"
                    min="1"
                    value={newCode.max_uses}
                    onChange={(e) => setNewCode({ ...newCode, max_uses: parseInt(e.target.value) })}
                    required
                    className="mt-1"
                  />
                  <p className="text-xs text-[#57534E] mt-1">1 = One-time gift</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#2D2D2D]">Tier Granted</label>
                  <select
                    value={newCode.tier_granted}
                    onChange={(e) => setNewCode({ ...newCode, tier_granted: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-md"
                  >
                    <option value="tier1">Essential</option>
                    <option value="tier2">Premium</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2D2D2D]">Duration (days)</label>
                  <Input
                    type="number"
                    min="1"
                    value={newCode.duration_days}
                    onChange={(e) => setNewCode({ ...newCode, duration_days: parseInt(e.target.value) })}
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-[#2D2D2D]">Expiration Date (optional)</label>
                <Input
                  type="date"
                  value={newCode.expires_at}
                  onChange={(e) => setNewCode({ ...newCode, expires_at: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-[#2D2D2D]">Description</label>
                <Textarea
                  value={newCode.description}
                  onChange={(e) => setNewCode({ ...newCode, description: e.target.value })}
                  placeholder="e.g., Gift code for early supporters"
                  className="mt-1"
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-primary text-white">
                  Create Code
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminPromoCodesPage;
